import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Lock, Clock, FileText, Award, AlertCircle } from "lucide-react";

function fmtSec(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/course"), api.get("/progress"), api.get("/settings/public")]).then(([c, p, s]) => {
      setCourse(c.data);
      setProgress(p.data);
      window.__demo = s.data.demo_mode;
    });
  }, []);

  if (!course || !progress) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">Loading curriculum…</div>;
  }

  const trackMult = user?.track === "renewal" ? 0.5 : 1;
  const demo = !!window.__demo;
  const modulesPassed = course.modules.filter((m) => progress.modules[m.id].quiz_passed).length;
  const allModulesPassed = modulesPassed === course.modules.length;
  const finalPassed = progress.final_exam.passed;
  const totalSeconds = course.modules.reduce((acc, m) => acc + (progress.modules[m.id].seconds_spent || 0), 0);
  const requiredTotal = demo ? 10 * course.modules.length : Math.round(course.total_seconds * trackMult);
  const hoursMet = totalSeconds >= requiredTotal;
  const pct = Math.min(100, Math.round((totalSeconds / requiredTotal) * 100));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="border-b border-slate-200 pb-6 mb-10">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Student Dashboard</div>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Welcome, {user.name.split(" ")[0]}
          </h1>
          <span data-testid="track-badge" className={`text-xs font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-sm ${user.track === "renewal" ? "bg-blue-50 text-blue-800 border border-blue-200" : "bg-slate-100 text-slate-700 border border-slate-200"}`}>
            {user.track === "renewal" ? "Renewal · 4-Hour" : "Initial · 8-Hour"}
          </span>
        </div>
        <p className="text-slate-600 mt-2 text-sm">
          Complete all six modules and the final exam to receive your {user.track === "renewal" ? "4-hour" : "8-hour"} completion acknowledgement.
        </p>
      </div>

      {/* Progress strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        <Metric icon={Clock} label="Time logged" value={fmtSec(totalSeconds)} sub={`${pct}% of ${user.track === "renewal" ? "4h" : "8h"}`} />
        <Metric icon={CheckCircle2} label="Modules passed" value={`${modulesPassed} / ${course.modules.length}`} />
        <Metric icon={FileText} label="Final exam" value={finalPassed ? "Passed" : "Not taken"} />
        <Metric icon={Award} label="Certificate" value={finalPassed && hoursMet ? "Ready" : "Locked"} />
      </div>

      <div className="w-full bg-slate-100 h-2 rounded-sm mb-10 overflow-hidden">
        <div className="bg-slate-900 h-2" style={{ width: `${pct}%` }} data-testid="overall-progress-bar" />
      </div>

      {/* Modules */}
      <div className="space-y-4">
        {course.modules.map((m, idx) => {
          const mp = progress.modules[m.id];
          const minSec = demo ? 10 : Math.round(m.min_seconds * trackMult);
          const prevPassed = idx === 0 || progress.modules[course.modules[idx - 1].id].quiz_passed;
          const locked = !prevPassed;
          const status = mp.quiz_passed ? "passed" : locked ? "locked" : "open";
          return (
            <div
              key={m.id}
              data-testid={`module-row-${m.id}`}
              className={`bg-white border rounded-sm p-6 flex flex-col md:flex-row md:items-center gap-6 ${
                status === "passed" ? "border-green-200" : status === "locked" ? "border-slate-200 bg-slate-50" : "border-slate-300"
              }`}
            >
              <div className="flex items-start gap-4 flex-1">
                <div className={`h-12 w-12 flex-none flex items-center justify-center rounded-sm ${
                  status === "passed" ? "bg-green-100 text-green-700" :
                  status === "locked" ? "bg-slate-200 text-slate-400" : "bg-slate-900 text-white"
                }`}>
                  {status === "passed" ? <CheckCircle2 className="h-6 w-6" /> :
                   status === "locked" ? <Lock className="h-5 w-5" /> :
                   <span className="font-mono font-bold">{String(m.order).padStart(2, "0")}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                    Module {m.order} · {m.duration_minutes} min
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {m.title}
                  </h3>
                  <p className="text-sm text-slate-600 mt-2">{m.summary}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                    <span>Time: <span className="font-mono text-slate-700">{fmtSec(mp.seconds_spent)}</span> / {fmtSec(minSec)}</span>
                    {mp.quiz_score != null && (
                      <span>Quiz: <span className="font-mono text-slate-700">{Math.round(mp.quiz_score * 100)}%</span></span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                {status === "locked" ? (
                  <span className="inline-flex items-center gap-2 text-sm text-slate-500 bg-slate-100 border border-slate-200 px-4 py-2 rounded-sm">
                    <Lock className="h-4 w-4" /> Locked
                  </span>
                ) : (
                  <Link
                    to={`/module/${m.id}`}
                    data-testid={`module-open-${m.id}`}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-medium text-sm transition-colors ${
                      status === "passed"
                        ? "border border-slate-300 bg-white text-slate-900 hover:bg-slate-50"
                        : "bg-slate-900 text-white hover:bg-slate-800"
                    }`}
                  >
                    {status === "passed" ? "Review" : "Begin module"}
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Final exam + certificate */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`bg-white border rounded-sm p-6 ${allModulesPassed ? "border-slate-300" : "border-slate-200 bg-slate-50"}`}>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Step 7</div>
          <h3 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Final Exam</h3>
          <p className="text-sm text-slate-600 mt-2">
            {course.final_exam_count} questions covering all six modules. 80% required to pass.
          </p>
          <div className="mt-4 flex items-center gap-3">
            {allModulesPassed ? (
              <Link to="/final-exam" data-testid="open-final-exam"
                className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-sm font-medium text-sm transition-colors">
                {finalPassed ? "Retake exam" : "Take final exam"}
              </Link>
            ) : (
              <span className="inline-flex items-center gap-2 text-sm text-slate-500 bg-slate-100 border border-slate-200 px-4 py-2 rounded-sm">
                <Lock className="h-4 w-4" /> Pass all modules first
              </span>
            )}
            {finalPassed && (
              <span className="text-sm text-green-700">
                ✓ Passed ({Math.round(progress.final_exam.score * 100)}%)
              </span>
            )}
          </div>
        </div>

        <div className={`bg-white border rounded-sm p-6 ${finalPassed && hoursMet ? "border-slate-300" : "border-slate-200 bg-slate-50"}`}>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Step 8</div>
          <h3 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Acknowledgement Letter</h3>
          <p className="text-sm text-slate-600 mt-2">
            Sign and download your 8-hour completion attestation.
          </p>
          <div className="mt-4">
            {finalPassed && hoursMet ? (
              <Link to="/certificate" data-testid="open-certificate"
                className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-sm font-medium text-sm transition-colors">
                Sign & Print
              </Link>
            ) : (
              <div className="inline-flex items-start gap-2 text-sm text-slate-600 bg-amber-50 border border-amber-200 px-4 py-3 rounded-sm">
                <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                <span>
                  Requires final exam passed and full 8 hours logged.
                  {!hoursMet && <> {fmtSec(requiredTotal - totalSeconds)} remaining.</>}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Range Day card */}
      {finalPassed && (
        <div className="mt-6 bg-white border border-slate-300 rounded-sm p-6">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Step 9 · Live-Fire Qualification</div>
          <h3 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
            Book your Range Day
          </h3>
          <p className="text-sm text-slate-600 mt-2">
            Self-book a date with an issuing-agency-approved instructor for the live-fire qualification course of fire.
          </p>
          <Link to="/range" data-testid="open-range-booking" className="inline-block mt-4 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-sm font-medium text-sm">
            Open Range Booking
          </Link>
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <Icon className="h-5 w-5 text-blue-700 mb-3" />
      <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
